import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { KnowledgeBadge } from "@/components/KnowledgeBadge";

/**
 * MUKTI 2.0 — Evidence Lens card.
 *
 * Standardised five-slot card used throughout Lead Detail and Case Workspace.
 * Slots in strict order:
 *   1. "What MUKTI noticed"    (title + icon)
 *   2. "Why it may matter"     (one-sentence muted context)
 *   3. Supporting records       (count badge, expands)
 *   4. Sources                  (publication / report-id chips)
 *   5. Status pill              (Unreviewed / Relevant / Not relevant / Watch /
 *                                Added to case)
 *
 * The card height is uniform whether the supporting evidence count is 1 or 100
 * — the records section expands inline on toggle.
 */
export function EvidenceLens({
  title,
  icon: Icon,
  whyItMatters,
  supportingRecords = [],
  sources = [],
  status = "unreviewed",
  knowledgeKind = "mukti_inference",
  renderRecord,
  onStatusChange,
  onRecordVerdict,
  testid = "evidence-lens",
}) {
  const [open, setOpen] = useState(false);
  const n = supportingRecords.length;
  return (
    <div
      data-testid={testid}
      className="border border-slate-200 rounded-lg bg-white shadow-sm overflow-hidden"
    >
      {/* Slot 1 + 2 */}
      <div className="flex items-start gap-3 px-4 py-3">
        {Icon ? (
          <div className="h-8 w-8 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
            <Icon className="h-4 w-4 text-slate-600" />
          </div>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-display font-semibold text-sm text-slate-900">{title}</h4>
            <KnowledgeBadge kind={knowledgeKind} compact />
            <StatusPill status={status} onChange={onStatusChange} />
          </div>
          {whyItMatters ? (
            <p className="mt-1 text-xs text-slate-500 leading-relaxed">{whyItMatters}</p>
          ) : null}
        </div>
      </div>

      {/* Slot 3: supporting records expander */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-expand`}
        className="w-full flex items-center justify-between gap-2 px-4 py-2 bg-slate-50 border-t border-slate-100 hover:bg-slate-100 text-xs"
      >
        <span className="flex items-center gap-1 text-slate-700">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Supporting records
          <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-900 text-white font-mono text-[10px]">{n}</span>
        </span>
        {/* Slot 4: sources — shown collapsed as chips */}
        <span className="flex flex-wrap items-center gap-1 justify-end">
          {sources.slice(0, 4).map((s) => (
            <span
              key={s}
              className="px-1.5 py-0.5 rounded border border-slate-200 bg-white text-[10px] font-mono text-slate-700"
            >
              {s}
            </span>
          ))}
          {sources.length > 4 ? (
            <span className="text-[10px] text-slate-500">+{sources.length - 4}</span>
          ) : null}
        </span>
      </button>

      {open ? (
        <div className="border-t border-slate-100 divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
          {n === 0 ? (
            <p className="px-4 py-3 text-xs text-slate-500 italic">No supporting records yet.</p>
          ) : (
            supportingRecords.map((r, i) => (
              <div key={r.id || `rec-${i}`} className="px-4 py-2 bg-white hover:bg-slate-50">
                {renderRecord
                  ? renderRecord(r, i)
                  : <DefaultRecord record={r} onVerdict={onRecordVerdict} />}
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

const STATUS_OPTIONS = [
  { k: "unreviewed", label: "Unreviewed", classes: "bg-slate-100 text-slate-700 border-slate-200" },
  { k: "relevant", label: "Relevant", classes: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { k: "not_relevant", label: "Not relevant", classes: "bg-slate-100 text-slate-500 line-through border-slate-200" },
  { k: "watch", label: "Watch", classes: "bg-amber-50 text-amber-800 border-amber-200" },
  { k: "added_to_case", label: "Added to case", classes: "bg-blue-50 text-blue-800 border-blue-200" },
];

function StatusPill({ status, onChange }) {
  const spec = STATUS_OPTIONS.find((s) => s.k === status) || STATUS_OPTIONS[0];
  if (!onChange) {
    return (
      <span
        data-testid={`evidence-status-${status}`}
        className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${spec.classes}`}
      >
        {spec.label}
      </span>
    );
  }
  return (
    <select
      value={status}
      data-testid="evidence-status-select"
      onChange={(e) => onChange(e.target.value)}
      className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border cursor-pointer ${spec.classes}`}
    >
      {STATUS_OPTIONS.map((s) => (
        <option key={s.k} value={s.k}>{s.label}</option>
      ))}
    </select>
  );
}

const VERDICT_OPTIONS = [
  { k: "unreviewed",    label: "Unreviewed",    classes: "bg-slate-100 text-slate-700 border-slate-200" },
  { k: "relevant",      label: "Relevant",      classes: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { k: "not_relevant",  label: "Not relevant",  classes: "bg-slate-100 text-slate-500 line-through border-slate-200" },
  { k: "watch",         label: "Watch",         classes: "bg-amber-50 text-amber-800 border-amber-200" },
  { k: "added_to_case", label: "Added to case", classes: "bg-blue-50 text-blue-800 border-blue-200" },
];

function RecordVerdictPicker({ record, onChange }) {
  const current = record.officer_verdict || "unreviewed";
  const spec = VERDICT_OPTIONS.find((o) => o.k === current) || VERDICT_OPTIONS[0];
  return (
    <select
      value={current}
      data-testid={`record-verdict-${record.id}`}
      onChange={(e) => onChange(record.id, e.target.value)}
      onClick={(e) => e.stopPropagation()}
      className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border cursor-pointer ${spec.classes}`}
    >
      {VERDICT_OPTIONS.map((o) => (
        <option key={o.k} value={o.k}>{o.label}</option>
      ))}
    </select>
  );
}

function DefaultRecord({ record, onVerdict }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-900 truncate">{record.title || record.id}</p>
        {record.posted_at ? (
          <p className="text-[11px] text-slate-500 font-mono">
            {record.posted_at.slice(0, 10)}
            {record.locality ? ` · ${record.locality}` : ""}
            {record.source ? ` · ${record.source}` : ""}
          </p>
        ) : null}
        {record.officer_note ? (
          <p className="text-[11px] text-amber-700 italic mt-0.5">
            Officer note: {record.officer_note}
          </p>
        ) : null}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {record.knowledge_type ? (
          <KnowledgeBadge kind={record.knowledge_type} compact />
        ) : null}
        {onVerdict ? (
          <RecordVerdictPicker record={record} onChange={onVerdict} />
        ) : null}
      </div>
    </div>
  );
}
